import { type ChangeEvent, useEffect, useState } from 'react'

import { FvButton, Icon } from '@fv/client-components'

import { ShipperSlider } from '@/components/ShipperSlider'
import { supportMessage } from '@/constants'
import {
  type ExportListType,
  useExportList,
} from '@/features/shipment-list/hooks/useExportList'
import { useAppContext } from '@/hooks/settings'

import InfoBox from './InfoBox'
import { RadioItem } from './inputs/RadioField'
import { List, ListItem } from './List'
import { Pinline } from './Pinline'

type ExportOptionsProps = {
  closePanel: () => void
  isOpen: boolean
  queryString: string
  listType?: ExportListType
}

const ExportOptions = ({
  closePanel,
  isOpen,
  queryString,
  listType = 'ready-completed',
}: ExportOptionsProps) => {
  const contextQuery = useAppContext()
  const email = contextQuery.data?.user.email
  const exportList = useExportList()

  const prevExportType =
    listType === 'in-process'
      ? 'advanced'
      : localStorage.getItem('fvExportType') || 'basic'

  const [exportType, setExportType] = useState(prevExportType)
  function updateAdvancedExport(e: ChangeEvent<HTMLInputElement>) {
    setExportType(e.target.value)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: bypass
  useEffect(() => {
    if (!isOpen) exportList.reset()
  }, [isOpen])

  function exportShipmentList() {
    localStorage.setItem('fvExportType', exportType)
    if (exportList.isPending) return
    exportList.mutate({
      queryString,
      list: listType,
      advancedExport: exportType === 'advanced',
    })
  }

  return (
    <ShipperSlider onClose={closePanel} isOpen={isOpen}>
      <div className="flex items-center">
        <h6 className="mb-0">
          <Icon icon="ballot-check" className="text-fv-gray-dark" />
          <span>The export will be emailed to {email ?? 'you'}.</span>
        </h6>
      </div>
      <Pinline className="mt-4 mb-8" />
      {!exportList.isSuccess && (
        <>
          {listType === 'ready-completed' && (
            <>
              <RadioItem
                id="basic-export"
                value="basic"
                name="basicExport"
                label="Basic export"
                checked={exportType !== 'advanced'}
                aria-describedby="helper-radio-text"
                onChange={updateAdvancedExport}
              />

              <Pinline rule />

              <div className="flex items-start">
                <Icon
                  icon="arrow-right"
                  className="text-fv-gray"
                  transform="down-2.5"
                />
                <p className="mb-0">
                  Export shipment data as a comma separated value (.csv) file.
                </p>
              </div>

              <Pinline className="!border-fv-orange my-8" />
            </>
          )}
          <RadioItem
            id="advanced-export"
            value="advanced"
            name="advancedExport"
            label="Advanced export"
            checked={exportType === 'advanced'}
            aria-describedby="helper-radio-text"
            onChange={updateAdvancedExport}
          />

          <Pinline rule />

          <List fcp0 className="mb-8">
            <ListItem itemsStart>
              <Icon
                icon="arrow-right"
                className="text-fv-gray"
                transform="down-2.5"
              />
              <p className="mb-0">
                Export shipment data as an Excel (.xlsx) file, with additional
                data:
              </p>
            </ListItem>
            <ListItem itemsStart dashed>
              <Icon
                icon="arrow-right"
                className="text-fv-gray"
                transform="down-2.5"
              />
              <p className="mb-0">
                Truckload route stops (location address, contact information,
                etc)
              </p>
            </ListItem>
            <ListItem itemsStart dashed>
              <Icon
                icon="arrow-right"
                className="text-fv-gray"
                transform="down-2.5"
              />
              <p className="mb-0">
                Shipping items (pallets, freight classes, weights, dimensions,
                etc)
              </p>
            </ListItem>
            <ListItem itemsStart dashed>
              <Icon
                icon="arrow-right"
                className="text-fv-gray"
                transform="down-2.5"
              />
              <p className="mb-0">
                Quotes (carrier, service, equipment, rate total)
              </p>
            </ListItem>
            <ListItem itemsStart dashed>
              <Icon
                icon="arrow-right"
                className="text-fv-gray"
                transform="down-2.5"
              />
              <p className="mb-0">
                {' '}
                Itemized charges (linehaul, fuel, accessorials, etc)
              </p>
            </ListItem>
            <ListItem itemsStart dashed>
              <Icon
                icon="arrow-right"
                className="text-fv-gray"
                transform="down-2.5"
              />
              <p className="mb-0"> Tender rejections (carrier)</p>
            </ListItem>
          </List>
          <Pinline rule />
        </>
      )}

      {exportList.isSuccess ? (
        <>
          <InfoBox
            icon="check-circle"
            iconProps={{ className: 'text-fv-green' }}
          >
            Freightview is in the process of exporting a custom{' '}
            {exportType === 'advanced' ? '.xlsx' : 'csv'} file to your email.
            You can continue using Freightview. Your custom{' '}
            {exportType === 'advanced' ? '.xlsx' : 'csv'} file should be in your
            inbox within a few minutes.
          </InfoBox>
          <FvButton icon="check" theme="default" onClick={closePanel}>
            <span>OK</span>
          </FvButton>
        </>
      ) : (
        <div className="flex items-center">
          <FvButton
            theme="primary"
            icon={exportList.isPending ? 'spinner' : 'file-export'}
            onClick={exportShipmentList}
          >
            <span>Export</span>
          </FvButton>
          <FvButton
            theme="plain"
            icon="times"
            onClick={e => {
              closePanel()
            }}
          >
            <span>Cancel</span>
          </FvButton>
        </div>
      )}

      {exportList.isError && (
        <InfoBox icon="triangle">
          Unable to export list, {supportMessage}
        </InfoBox>
      )}
    </ShipperSlider>
  )
}

export default ExportOptions
